/**
 * HVP Client Cascade - variation matching
 *
 * Exact-match lookup over the preloaded dataset plus the Smart Cascade
 * compatibility wrapper and the selection filter used by getOptions().
 *
 * Split out of client-cascade.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPClientCascade.prototype by
 * client-cascade.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const ClientCascadeMatchMethods = {
    /**
     * Find matching variation for complete selections
     *
     * @param {Object} selections - Complete attribute selections
     * @returns {Object|null} Matching variation or null
     */
    findVariation(selections) {
        const start = performance.now();

        // Normalize all selection keys
        const normalizedSelections = {};
        for (const [key, value] of Object.entries(selections)) {
            normalizedSelections[this.normalizeAttributeName(key)] = value;
        }

        // Find the variation that matches ALL selections. Like WooCommerce's
        // find_matching_product_variation, an exact match wins over a wildcard
        // ('' = "Any …") match, and among wildcard matches the fewest wildcards win.
        let best = null;
        let bestWildcards = Infinity;
        for (const variation of this.allVariations) {
            if (!variation.attributes) continue;

            let allMatch = true;
            let wildcards = 0;

            // Check each attribute in selections
            for (const [attr, value] of Object.entries(normalizedSelections)) {
                let variationValue = null;

                // Find the attribute value in variation
                for (const [vKey, vVal] of Object.entries(variation.attributes)) {
                    if (this.normalizeAttributeName(vKey) === attr) {
                        variationValue = vVal;
                        break;
                    }
                }

                if (variationValue === value) continue;
                if (variationValue === '') { wildcards++; continue; }
                allMatch = false;
                break;
            }

            if (allMatch && wildcards < bestWildcards) {
                best = variation;
                bestWildcards = wildcards;
                if (wildcards === 0) break;
            }
        }

        if (best) {
            const elapsed = Math.round(performance.now() - start);
            this.log(`Found matching variation ${best.variation_id} (wildcards=${bestWildcards}) in ${elapsed}ms`);
            return best;
        }

        const elapsed = Math.round(performance.now() - start);
        this.log(`No matching variation found in ${elapsed}ms`);
        return null;
    },

    /**
     * Check if selections are compatible (Smart Cascade)
     *
     * @param {Object} selections - Complete selections to validate
     * @returns {Object} Compatibility result
     */
    checkCompatibility(selections) {
        const start = performance.now();

        // Try to find a matching variation
        const variation = this.findVariation(selections);

        const result = {
            isCompatible: variation !== null,
            variation: variation,
            fullSelections: selections,
            checkedAt: Date.now()
        };

        const elapsed = Math.round(performance.now() - start);
        this.log(`Compatibility check: ${result.isCompatible ? 'COMPATIBLE' : 'INCOMPATIBLE'} in ${elapsed}ms`);

        return result;
    },

    /**
     * Filter variations by selections
     *
     * @param {Object} selections - Attribute selections to filter by
     * @returns {Array} Filtered variations
     */
    filterVariations(selections) {
        // If no selections, return all variations
        if (!selections || Object.keys(selections).length === 0) {
            return this.allVariations;
        }

        return this.allVariations.filter(variation => {
            if (!variation.attributes) return false;

            // Check each selection
            for (const [attr, value] of Object.entries(selections)) {
                const normalized = this.normalizeAttributeName(attr);
                let variationValue = null;

                // Find the attribute value in variation
                for (const [vKey, vVal] of Object.entries(variation.attributes)) {
                    if (this.normalizeAttributeName(vKey) === normalized) {
                        variationValue = vVal;
                        break;
                    }
                }

                // '' is a wildcard ("Any …") — it matches every selection.
                // A missing attribute (null) never matches (Codex F02).
                if (variationValue !== value && variationValue !== '') {
                    return false;
                }
            }

            return true;
        });
    },
};

export default ClientCascadeMatchMethods;
